import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Toaster } from './components/ui/sonner';
import { toast } from 'sonner';

// i18n
import './i18n';

// Components
import PushNotificationProvider from './components/PushNotificationProvider';
import ScrollToTop from './components/ScrollToTop';

// Pages
import HomePage from './pages/HomePage';
import HowItWorksPage from './pages/HowItWorksPage';
import PropertiesPage from './pages/PropertiesPage';
import PropertyDetailPage from './pages/PropertyDetailPage';
import LoginPage from './pages/LoginPage';
import RegisterBuyerPage from './pages/RegisterBuyerPage';
import RegisterAgentPage from './pages/RegisterAgentPage';
import RegisterSellerPage from './pages/RegisterSellerPage';
import EmailVerificationPage from './pages/EmailVerificationPage';
import BuyerDashboard from './pages/buyer/BuyerDashboard';
import AgentDashboard from './pages/agent/AgentDashboard';
import AdminDashboard from './pages/admin/AdminDashboard';
import SellerDashboard from './pages/seller/SellerDashboard';
import FAQPage from './pages/FAQPage';
import ContactPage from './pages/ContactPage';
import AnalyticsDashboard from './pages/admin/AnalyticsDashboard';
import GareDiVenditaPage from './pages/GareDiVenditaPage';
import GaraDetailPage from './pages/GaraDetailPage';
import PrivacyDashboard from './pages/PrivacyDashboard';
import TermsOfServicePage from './pages/TermsOfServicePage';
import CookiePolicyPage from './pages/CookiePolicyPage';
import PrivacyPolicyPage from './pages/PrivacyPolicyPage';
import AIServicesPage from './pages/AIServicesPage';
import InvestorPitchPage from './pages/InvestorPitchPage';
import DemoPage from './pages/DemoPage';

// Context
const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// Configure axios defaults
axios.defaults.baseURL = API;

// Auth Provider
const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const refreshTokenRef = useRef(localStorage.getItem('refresh_token'));

  // Function to refresh the access token
  const refreshAccessToken = useCallback(async () => {
    const refreshToken = refreshTokenRef.current || localStorage.getItem('refresh_token');
    if (!refreshToken) {
      return null;
    }

    try {
      const response = await axios.post('/auth/refresh', { refresh_token: refreshToken });
      const { access_token } = response.data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      return access_token;
    } catch (error) {
      // Refresh failed - clear all tokens
      localStorage.removeItem('token');
      localStorage.removeItem('refresh_token');
      setToken(null);
      setUser(null);
      refreshTokenRef.current = null;
      delete axios.defaults.headers.common['Authorization'];
      return null;
    }
  }, []);

  const login = useCallback(async (email, password, deviceId = null) => {
    try {
      const headers = deviceId ? { 'X-Device-ID': deviceId } : {};
      const response = await axios.post('/auth/login', { email, password }, { headers });
      
      // Check if 2FA is required
      if (response.data.requires_2fa) {
        return {
          requires_2fa: true,
          temp_token: response.data.temp_token,
          message: response.data.message
        };
      }
      
      const { access_token, refresh_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      if (refresh_token) {
        localStorage.setItem('refresh_token', refresh_token);
        refreshTokenRef.current = refresh_token;
      }
      
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      return userData;
    } catch (error) {
      throw error;
    }
  }, []);

  const complete2FALogin = useCallback(async (tempToken, code, trustDevice = false, deviceId = null) => {
    try {
      const response = await axios.post('/auth/2fa/login-verify', {
        temp_token: tempToken,
        code,
        trust_device: trustDevice,
        device_id: deviceId
      });
      
      const { access_token, refresh_token, user: userData } = response.data;
      
      localStorage.setItem('token', access_token);
      if (refresh_token) {
        localStorage.setItem('refresh_token', refresh_token);
        refreshTokenRef.current = refresh_token;
      }
      
      setToken(access_token);
      setUser(userData);
      axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
      return userData;
    } catch (error) {
      throw error;
    }
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    setToken(null);
    setUser(null);
    refreshTokenRef.current = null;
    delete axios.defaults.headers.common['Authorization'];
  }, []);

  // Set up axios interceptor for automatic token refresh
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        
        // If token expired (401) and we haven't retried yet
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true;
          
          const newToken = await refreshAccessToken();
          if (newToken) {
            originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
            return axios(originalRequest);
          }
        }
        
        return Promise.reject(error);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [refreshAccessToken]);

  const checkAuth = useCallback(async () => {
    const storedToken = localStorage.getItem('token');
    if (!storedToken) {
      setLoading(false);
      return;
    }

    try {
      axios.defaults.headers.common['Authorization'] = `Bearer ${storedToken}`;
      const response = await axios.get('/auth/me');
      setUser(response.data);
      setToken(storedToken);
    } catch (error) {
      // Try to refresh token before giving up
      const newToken = await refreshAccessToken();
      if (newToken) {
        try {
          const response = await axios.get('/auth/me');
          setUser(response.data);
          setToken(newToken);
        } catch {
          logout();
        }
      } else {
        logout();
      }
    } finally {
      setLoading(false);
    }
  }, [refreshAccessToken, logout]);

  useEffect(() => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const value = {
    user,
    setUser,
    token,
    setToken,
    loading,
    login,
    complete2FALogin,
    logout,
    checkAuth,
    isAuthenticated: !!user
  };

  return (
    <AuthContext.Provider value={value}>
      {user ? (
        <PushNotificationProvider userId={user.user_id}>
          {children}
        </PushNotificationProvider>
      ) : (
        children
      )}
    </AuthContext.Provider>
  );
};

// Protected Route
const ProtectedRoute = ({ children, allowedTypes = [] }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F4C5C]"></div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (allowedTypes.length > 0 && !allowedTypes.includes(user.user_type)) {
    return <Navigate to="/" replace />;
  }

  return children;
};

// Auth Callback for Google OAuth
const AuthCallback = () => {
  const { setUser, setToken } = useAuth();
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        try {
          const response = await axios.post(`/auth/google/session?session_id=${sessionId}`);
          const { access_token, user: userData } = response.data;
          localStorage.setItem('token', access_token);
          setToken(access_token);
          setUser(userData);
          axios.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;
          toast.success('Accesso effettuato con Google');
          
          // Redirect based on user type
          if (userData.user_type === 'agent') {
            navigate('/agent', { replace: true });
          } else if (userData.user_type === 'admin') {
            navigate('/admin', { replace: true });
          } else {
            navigate('/buyer', { replace: true });
          }
        } catch (error) {
          toast.error('Errore durante l\'accesso con Google');
          navigate('/login', { replace: true });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };

    processAuth();
  }, [navigate, setUser, setToken]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#F9F9F7]">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0F4C5C] mx-auto mb-4"></div>
        <p className="text-gray-600">Accesso in corso...</p>
      </div>
    </div>
  );
};

// Main App Router
function AppRouter() {
  const location = useLocation();
  const { token, user } = useAuth();

  // Check for OAuth callback
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  return (
    <>
      <ScrollToTop />
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
      <Route path="/come-funziona" element={<HowItWorksPage />} />
      <Route path="/immobili" element={<PropertiesPage />} />
      <Route path="/immobili/:propertyId" element={<PropertyDetailPage />} />
      <Route path="/faq" element={<FAQPage />} />
      <Route path="/contatti" element={<ContactPage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/registrati/acquirente" element={<RegisterBuyerPage />} />
      <Route path="/registrati/agente" element={<RegisterAgentPage />} />
      <Route path="/registrati/proprietario" element={<RegisterSellerPage />} />
      <Route path="/verifica-email" element={<EmailVerificationPage />} />
      
      {/* Gare di Vendita Routes (Public) */}
      <Route path="/gare" element={<GareDiVenditaPage />} />
      <Route path="/gara/:competitionId" element={<GaraDetailPage />} />
      
      {/* Legal & Policy Pages (Public) */}
      <Route path="/termini" element={<TermsOfServicePage />} />
      <Route path="/cookies" element={<CookiePolicyPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
      <Route path="/servizi-ai" element={<AIServicesPage />} />
      
      {/* Investor Pitch Page */}
      <Route path="/investors" element={<InvestorPitchPage />} />
      <Route path="/pitch" element={<InvestorPitchPage />} />
      <Route path="/demo" element={<DemoPage />} />
      
      {/* Protected Buyer Routes */}
      <Route path="/buyer/*" element={
        <ProtectedRoute allowedTypes={['buyer']}>
          <BuyerDashboard />
        </ProtectedRoute>
      } />
      
      {/* Protected Agent Routes */}
      <Route path="/agent/*" element={
        <ProtectedRoute allowedTypes={['agent']}>
          <AgentDashboard />
        </ProtectedRoute>
      } />
      
      {/* Protected Seller Routes */}
      <Route path="/seller/*" element={
        <ProtectedRoute allowedTypes={['seller']}>
          <SellerDashboard />
        </ProtectedRoute>
      } />
      
      {/* Protected Admin Routes */}
      <Route path="/admin/*" element={
        <ProtectedRoute allowedTypes={['admin']}>
          <AdminDashboard />
        </ProtectedRoute>
      } />
      <Route path="/admin/analytics" element={
        <ProtectedRoute allowedTypes={['admin']}>
          <AnalyticsDashboard />
        </ProtectedRoute>
      } />

      {/* Privacy Dashboard - Available to all authenticated users */}
      <Route path="/privacy" element={
        <ProtectedRoute allowedTypes={['admin', 'agent', 'seller', 'buyer']}>
          <PrivacyDashboard token={token} user={user} />
        </ProtectedRoute>
      } />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
